<template>
  <div class="modal is-fullscreen" :class="{ 'is-active': is_open }">
    <div class="modal-background" @click="close"></div>
    <div class="modal-card is-fullscreen">
      <header class="modal-card-head">
        <p class="modal-card-title">
          {{ is_editing ? $t("edit rule") : $t("new rule") }}
        </p>
        <button class="delete" @click="close"></button>
      </header>
      <section class="modal-card-body">
        <generic-tabs
          :tabs="[
            {
              name: 'tab1',
              label: $t('general information'),
              icon: 'fa fa-info',
            },
            { name: 'tab2', label: $t('code'), icon: 'fa fa-code' },
          ]"
          orientation="horizontal"
        >
          <template v-slot:tab1
            ><form @submit.prevent="save">
              <!-- Basic fields -->
              <div class="columns">
                <div class="column is-7">
                  <label class="label">{{ $t("name") }} *</label>
                  <div class="control">
                    <input
                      type="text"
                      class="input"
                      v-model="form.name"
                      :placeholder="$t('enter name')"
                      required
                    />
                  </div>
                </div>
                <div class="column">
                  <label class="label">{{ $t("status") }} *</label>
                  <div class="control">
                    <div class="select is-fullwidth">
                      <select v-model="form.status" required>
                        <option value="">{{ $t("select status") }}</option>
                        <option value="active">{{ $t("active") }}</option>
                        <option value="inactive">{{ $t("inactive") }}</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
              <div class="columns" v-if="form.settings">
                <div class="column">
                  <label class="label">{{ $t("options") }}</label>
                  <div class="control">
                    <div class="select is-fullwidth">
                      <select v-model="form.settings.type" required>
                        <option value="">{{ $t("select type") }}</option>
                        <option value="informative">
                          {{ $t("informative") }}
                        </option>
                        <option value="evaluative">
                          {{ $t("evaluative") }}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>
                <div class="column">
                  <label class="label">{{ $t("type") }} *</label>
                  <div class="field">
                    <input
                      class="is-checkradio"
                      type="checkbox"
                      id="chk_affects_general_score"
                      v-model="form.settings.affects_general_score"
                    />
                    <label for="chk_affects_general_score">{{
                      $t("affects general score")
                    }}</label>
                  </div>
                </div>
              </div>

              <div class="field">
                <label class="label">{{ $t("description") }}</label>
                <div class="control">
                  <textarea
                    class="textarea"
                    v-model="form.description"
                    :placeholder="$t('enter description')"
                    rows="2"
                  ></textarea>
                </div>
              </div>
            </form>
          </template>
          <template v-slot:tab2>
            <!-- Code -->
            <div class="title is-6">{{ $t("code") }} *</div>
            <div class="editor_container">
              <vue-monaco-editor
                v-model:value="form.code"
                theme="vs-dark"
                language="javascript"
                :options="{
                  automaticLayout: true,
                  formatOnType: true,
                  formatOnPaste: true,
                  tabSize: 2,
                  scrollBeyondLastLine: false,
                  folding: true,
                  bracketPairColorization: true,
                  autoClosingBrackets: 'always',
                  autoIndent: 'full',
                }"
                style="height: 500px"
              />
            </div>
            <div class="notification is-dark">
              <div class="title is-6 mb-2">{{ $t("hints") }}</div>
              <p>
                <span class="has-text-weight-bold has-text-info is-italic"
                  >direct_debt_history </span
                >{{ $t("for referring to the direct debt history.") }}
              </p>
              <p>
                <span class="has-text-weight-bold has-text-info is-italic"
                  >detalle_directo_vencido_cobro_judicial </span
                >{{
                  $t(
                    "for referring to the overdue judicial collection details."
                  )
                }}
              </p>
              <p>
                <span class="has-text-weight-bold has-text-info is-italic"
                  >detalle_directo_vencido_proceso_prorroga </span
                >{{
                  $t(
                    "for referring to the overdue direct debt extension process."
                  )
                }}
              </p>
              <p>
                <span class="has-text-weight-bold has-text-info is-italic"
                  >detalle_directo_vencido_cobro_administrativo </span
                >{{
                  $t(
                    "for referring to the overdue administrative collection details."
                  )
                }}
              </p>
              <p>
                <span class="has-text-weight-bold has-text-info is-italic"
                  >detalle_declarado_incobrable </span
                >{{ $t("for referring to the declared uncollectible debts.") }}
              </p>
              <p>
                <span class="has-text-weight-bold has-text-info is-italic"
                  >evaluation </span
                >{{ $t("for referring the evaluation.") }}
              </p>
              <p>
                <span class="has-text-weight-bold has-text-info is-italic"
                  >module_settings </span
                >{{ $t("for referring to the module settings") }}
              </p>
              <p>
                <span class="has-text-weight-bold has-text-info is-italic"
                  >axios </span
                >{{ $t("for referring to the axios instance") }}
              </p>
              <p>
                <span class="has-text-weight-bold has-text-info is-italic"
                  >comission </span
                >{{ $t("for referring to the comission") }}
              </p>
              <p>
                <span class="has-text-weight-bold has-text-info is-italic"
                  >rounding_quota </span
                >{{ $t("for referring to the rounding quota") }}
              </p>
              <p>
                <span class="has-text-weight-bold has-text-info is-italic"
                  >final_limit </span
                >{{ $t("for referring to the final limit") }}
              </p>
              <p>
                <span class="has-text-weight-bold has-text-info is-italic"
                  >inferred_income </span
                >{{ $t("for referring to the inferred income") }}
              </p>
              <p>
                <span class="has-text-weight-bold has-text-info is-italic"
                  >score </span
                >{{ $t("for referring to the score") }}
              </p>
              <p>
                <span class="has-text-weight-bold has-text-info is-italic"
                  >rate </span
                >{{ $t("for referring to the rate") }}
              </p>
              <p>
                <span class="has-text-weight-bold has-text-warning is-italic"
                  >resolve ({ check: true }) </span
                >{{ $t("call resolve function to approve rule") }}
              </p>
              <p>
                <span class="has-text-weight-bold has-text-warning is-italic"
                  >resolve ({ check: false, data: [] }) </span
                >{{ $t("call resolve function to reject rule") }}
              </p>
            </div>
          </template>
        </generic-tabs>
      </section>

      <footer class="modal-card-foot">
        <button
          class="button is-success is-rounded"
          @click="save"
          :disabled="is_loading"
        >
          <span class="icon" v-if="is_loading">
            <i class="fa fa-spinner fa-spin"></i>
          </span>
          <span class="icon" v-else>
            <i class="fa fa-save"></i>
          </span>
          <span>{{ $t("save") }}</span>
        </button>
        <button class="button is-rounded ml-3" @click="close">
          {{ $t("cancel") }}
        </button>
      </footer>
    </div>
  </div>
</template>

<script>
import { VueMonacoEditor } from "@guolao/vue-monaco-editor";
import GenericTabs from "@/components/generic_tabs/Index.vue";

import {
  get,
  post,
  put,
  success_message,
  error_message,
  format_date,
} from "@/utils";

export default {
  name: "CreditAnalysisRuleModal",
  components: {
    VueMonacoEditor,
    GenericTabs,
  },
  data() {
    return {
      is_open: false,
      is_editing: false,
      is_loading: false,
      uuid: null,
      active_rule_tab: 0,
      form: {
        name: "",
        description: "",
        code: "",
        status: "active",
        settings: {},
      },
    };
  },
  methods: {
    open(uuid = null) {
      this.uuid = uuid;
      this.is_editing = !!uuid;
      this.is_open = true;

      if (this.is_editing) {
        this.loadRule();
      } else {
        this.resetForm();
      }
    },
    close() {
      this.is_open = false;
      this.resetForm();
    },
    resetForm() {
      this.form = {
        name: "",
        code: "",
        description: "",
        status: "active",
        settings: {},
      };
      this.active_rule_tab = 0;
    },
    loadRule() {
      this.is_loading = true;
      get("/credit-analysis/rule", { uuid: this.uuid })
        .then((response) => {
          const data = response;
          this.form = {
            name: data.name || "",
            description: data.description || "",
            code: data.code || "",
            status: data.status || "",
            settings: data.settings || {},
          };
        })
        .catch((error) => {
          error_message(error);
        })
        .finally(() => {
          this.is_loading = false;
        });
    },
    save() {
      if (!this.form.name.trim()) {
        error_message(this.$t("name is required"));
        return;
      }

      this.is_loading = true;
      // const settings = { ...this.form.settings };
      const data = { ...this.form };
      const request = this.is_editing
        ? post("/credit-analysis/rule", { ...data, uuid: this.uuid })
        : put("/credit-analysis/rule", data);

      request
        .then(() => {
          success_message(
            this.$t(
              this.is_editing
                ? "rule updated successfully"
                : "rule created successfully"
            )
          );
          this.$emit("saved");
          this.close();
        })
        .catch((error) => {
          error_message(error);
        })
        .finally(() => {
          this.is_loading = false;
        });
    },
  },
};
</script>

<style scoped>
.modal.is-fullscreen .modal-card {
  width: 90vw;
  height: 90vh;
  max-width: 90vw;
  max-height: 90vh;
  margin: 0;
  border-radius: 15px;
}

.modal.is-fullscreen .modal-card-body {
  max-height: calc(100vh - 120px);
  overflow-y: auto;
  padding: 2rem;
}

.modal.is-fullscreen .modal-card-head {
  border-radius: 0;
}

.modal.is-fullscreen .modal-card-foot {
  border-radius: 0;
}

.help {
  font-size: 0.875rem;
  color: #888;
}

.field {
  margin-bottom: 1.5rem;
}

/* Vertical tabs styling */
.tabs.is-vertical {
  display: block;
}

.tabs.is-vertical ul {
  flex-direction: column;
  border-bottom: none;
  /* border-right: 1px solid #dbdbdb; */
}

.tabs.is-vertical li {
  display: block;
  width: 100%;
}

.tabs.is-vertical li:not(:last-child) {
  margin-right: 0;
  margin-bottom: 0;
}

.tabs.is-vertical li a {
  /* border-bottom: 1px solid #dbdbdb; */
  border-radius: 4px 0 0 4px;
  justify-content: flex-start;
  padding: 0.75em 1em;
  display: flex;
  align-items: center;
}

.tabs.is-vertical li.is-active a {
  background-color: #3273dc;
  border-color: #3273dc;
  color: #fff;
}

.tabs.is-vertical li a:hover {
  background-color: #646060;
  border-color: #b5b5b5;
}

.tabs.is-vertical li.is-active a:hover {
  background-color: #2366d1;
  border-color: #2366d1;
}

.tab-link {
  position: relative;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.rule-content {
  min-height: 400px;
}

.delete.is-small {
  width: 16px;
  height: 16px;
  min-width: 16px;
  min-height: 16px;
}

.fixed-width-control {
  max-width: 150px;
}

.help {
  font-size: 0.875rem;
  color: #888;
}

.editor_container {
  border: 1px groove #0f9172ad;
  border-radius: 3px;
}
</style>
